import { readPendingRevitOperation } from '../../../api/grpc/operationProgressClient';
import {
  executeConnectorAdditionPlan,
  type ConnectorAdditionPlanDeps,
} from './connectorAdditionPlan';
import { notifyParameterAddition } from './feedback';
import {
  blockOnMismatchedPendingOperation,
  buildConnectorOperationUiSnapshot,
} from './pendingConnectorArbitration';
import {
  areConnectorExecutionContextsEqual,
  classifyPendingConnectorRecovery,
  type ConnectorExecutionContext,
  type ConnectorParameterInFlightEntry,
  type PendingConnectorRecoveryDisposition,
} from './pendingConnectorRecovery';
import {
  type ParameterAdditionOptions,
  type ParameterAdditionResult,
} from './models';
import { type ParameterAdditionCore } from './useParameterAdditionCore';

/**
 * Seams one connector invocation needs. The plan seams come from
 * `ConnectorAdditionPlanDeps`; the rest are the arbitration and lifecycle
 * collaborators the runner owns.
 */
export interface ConnectorInvocationDeps extends ConnectorAdditionPlanDeps {
  addParametersInFlightRef: ParameterAdditionCore['addParametersInFlightRef'];
  currentProjectUndoKey: ParameterAdditionCore['currentProjectUndoKey'];
  expectedDocumentIdentity: ParameterAdditionCore['expectedDocumentIdentity'];
  probePendingOperation: ParameterAdditionCore['probePendingOperation'];
  resumeExactPendingConnectorOperation: ParameterAdditionCore['resumeExactPendingConnectorOperation'];
  setActiveConnectorOperation: ParameterAdditionCore['setActiveConnectorOperation'];
  setConnectorSelectionBlockedByMismatch: ParameterAdditionCore['setConnectorSelectionBlockedByMismatch'];
  trackPendingOperationToTerminal: ParameterAdditionCore['trackPendingOperationToTerminal'];
}

export const CONCURRENT_MODE_CONFLICT_MESSAGE =
  'Another connector parameter operation is still running with a different mode. Wait for it to finish, then select Apply and continue again.';

export type ConcurrentInvocationDecision =
  | { kind: 'start' }
  | { kind: 'join'; promise: Promise<ParameterAdditionResult> }
  | { kind: 'refuse'; error: string; result: ParameterAdditionResult };

/**
 * Captures the mode, project, and document the user actually clicked Continue
 * on. Everything downstream reads this instead of live store state, so a value
 * that changes during an await cannot redirect a running mutation.
 */
export function freezeConnectorExecutionContext(
  options: ParameterAdditionOptions,
  deps: ConnectorInvocationDeps,
): ConnectorExecutionContext {
  const connectorTarget = options.connectorTarget ?? {
    destination: deps.connectorConfig.parameterDestination,
    bindingKind: deps.connectorConfig.parameterBindingKind,
  };
  return {
    owner: 'connector-parameters',
    projectKey: deps.currentProjectUndoKey ?? '',
    expectedDocumentName: deps.expectedDocumentIdentity?.expectedDocumentName ?? null,
    expectedDocumentPath: deps.expectedDocumentIdentity?.expectedDocumentPath ?? null,
    destination: connectorTarget.destination,
    bindingKind: connectorTarget.bindingKind,
  };
}

/**
 * Decides what a click does while another invocation is still running: join an
 * identical one so a double click cannot double-mutate, or refuse a differing
 * one rather than let two modes race the same Revit document.
 */
export function decideConcurrentInvocation(
  inFlight: ConnectorParameterInFlightEntry | null,
  frozenContext: ConnectorExecutionContext,
): ConcurrentInvocationDecision {
  if (!inFlight) {
    return { kind: 'start' };
  }

  if (areConnectorExecutionContextsEqual(inFlight.context, frozenContext)) {
    return { kind: 'join', promise: inFlight.promise };
  }

  return {
    kind: 'refuse',
    error: CONCURRENT_MODE_CONFLICT_MESSAGE,
    result: {
      success: false,
      addedCount: 0,
      blockedByActiveOperation: true,
      error: CONCURRENT_MODE_CONFLICT_MESSAGE,
    },
  };
}

/**
 * Sends a classified disposition down exactly one path: resume the recovered
 * operation, block on a foreign or mismatched one, or run the snapshot plan.
 */
function routeConnectorInvocation(
  options: ParameterAdditionOptions,
  frozenContext: ConnectorExecutionContext,
  disposition: PendingConnectorRecoveryDisposition,
  deps: ConnectorInvocationDeps,
): Promise<ParameterAdditionResult> {
  if (disposition.kind === 'exact-match') {
    return deps.resumeExactPendingConnectorOperation(disposition.record);
  }

  if (disposition.kind !== 'none') {
    return blockOnMismatchedPendingOperation(disposition, deps);
  }

  return executeConnectorAdditionPlan(options, frozenContext, deps);
}

/**
 * Publishes the operation UI state, registers the invocation so a concurrent
 * click can find it, and clears both once this invocation settles.
 */
async function startConnectorInvocation(
  options: ParameterAdditionOptions,
  frozenContext: ConnectorExecutionContext,
  deps: ConnectorInvocationDeps,
): Promise<ParameterAdditionResult> {
  const disposition = classifyPendingConnectorRecovery(
    readPendingRevitOperation(),
    frozenContext,
  );
  const uiSnapshot = buildConnectorOperationUiSnapshot(disposition, frozenContext);
  deps.setActiveConnectorOperation(uiSnapshot.activeOperation);
  deps.setConnectorSelectionBlockedByMismatch(uiSnapshot.selectionBlockedByMismatch);

  const invocation = routeConnectorInvocation(options, frozenContext, disposition, deps);
  deps.addParametersInFlightRef.current = { context: frozenContext, promise: invocation };
  try {
    return await invocation;
  } finally {
    if (deps.addParametersInFlightRef.current?.promise === invocation) {
      deps.addParametersInFlightRef.current = null;
      deps.setActiveConnectorOperation(null);
      deps.setConnectorSelectionBlockedByMismatch(false);
    }
  }
}

/**
 * Runs one connector parameter invocation end to end, from the context frozen
 * at click time to the terminal result.
 */
export async function runConnectorParameterAddition(
  options: ParameterAdditionOptions,
  deps: ConnectorInvocationDeps,
): Promise<ParameterAdditionResult> {
  const frozenContext = freezeConnectorExecutionContext(options, deps);
  const decision = decideConcurrentInvocation(
    deps.addParametersInFlightRef.current,
    frozenContext,
  );
  if (decision.kind === 'join') {
    return decision.promise;
  }
  if (decision.kind === 'refuse') {
    notifyParameterAddition(options, deps.showWarning, decision.error);
    return decision.result;
  }

  return startConnectorInvocation(options, frozenContext, deps);
}
